import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { WalletAuthProvider, useWalletAuth } from "./context/WalletAuthContext";

import Landing from "./pages/Landing";
import BenefitsEstimator from "./pages/BenefitsEstimator";
import EmployersPage from "./pages/EmployersPage";
import ForEmployersPage from "./pages/ForEmployersPage";
import WalletLogin from "./pages/wallet/WalletLogin";
import SuperAdminLogin from "./pages/wallet/SuperAdminLogin";
import ForcePasswordReset from "./pages/wallet/ForcePasswordReset";
import SuperAdminDashboard from "./pages/wallet/SuperAdminDashboard";
import CompanyManagement from "./pages/wallet/CompanyManagement";
import GlobalVendors from "./pages/wallet/GlobalVendors";
import ReconciliationPage from "./pages/wallet/ReconciliationPage";
import SuperAdminReports from "./pages/wallet/SuperAdminReports";
import AdminManagement from "./pages/wallet/AdminManagement";
import UserManagement from "./pages/wallet/UserManagement";
import AuditLogs from "./pages/wallet/AuditLogs";
import CompanyDashboard from "./pages/wallet/CompanyDashboard";
import EmployeeManagement from "./pages/wallet/EmployeeManagement";
import PayrollManagement from "./pages/wallet/PayrollManagement";
import CompanyWallets from "./pages/wallet/CompanyWallets";
import CompanyTransactions from "./pages/wallet/CompanyTransactions";
import CompanyVendors from "./pages/wallet/CompanyVendors";
import CompanyBenefits from "./pages/wallet/CompanyBenefits";
import CompanyPayouts from "./pages/wallet/CompanyPayouts";
import CompanyReports from "./pages/wallet/CompanyReports";
import EmployeeHome from "./pages/wallet/EmployeeHome";
import EmployeeTransactions from "./pages/wallet/EmployeeTransactions";
import EmployeeBenefits from "./pages/wallet/EmployeeBenefits";
import EmployeePayouts from "./pages/wallet/EmployeePayouts";
import EmployeeProfile from "./pages/wallet/EmployeeProfile";

// New Employee App Pages
import NewEmployeeHome from "./pages/employee/EmployeeHome";
import EmployeeWallet from "./pages/employee/EmployeeWallet";
import SendMoney from "./pages/employee/SendMoney";
import EmployeePay from "./pages/employee/EmployeePay";
import EmployeeBenefitsPlan from "./pages/employee/EmployeeBenefitsPlan";
import EmployeeVendors from "./pages/employee/EmployeeVendors";
import VendorDetailPage from "./pages/employee/VendorDetailPage";
import EmployeeProfilePage from "./pages/employee/EmployeeProfilePage";
import EmployeeOfferLetters from "./pages/employee/EmployeeOfferLetters";
import EmployeeFlexBenefits from "./pages/employee/EmployeeFlexBenefits";

// Offer Letter Admin Pages
import OfferLettersPage from "./pages/admin/OfferLettersPage";
import CreateOfferLetter from "./pages/admin/CreateOfferLetter";
import OfferLetterDetail from "./pages/admin/OfferLetterDetail";

// Public Candidate Page
import CandidateOfferPage from "./pages/CandidateOfferPage";

import WalletPayrollControls from "./pages/wallet/WalletPayrollControls";
import CompanySettings from "./pages/wallet/CompanySettings";
import BulkEmployeeUpload from "./pages/wallet/BulkEmployeeUpload";
import BankingManagement from "./pages/wallet/BankingManagement";
import FlexBenefitsAdmin from "./pages/wallet/FlexBenefitsAdmin";
import ForgotPassword from "./pages/wallet/ForgotPassword";
import CompanyDetail from "./pages/wallet/CompanyDetail";
import BankingPartners from "./pages/wallet/BankingPartners";
import UserAnalytics from "./pages/super-admin/UserAnalytics";
import WalletCredit from "./pages/super-admin/WalletCredit";
import UserDetailPage from "./pages/super-admin/UserDetailPage";
import VirtualAccountsOverview from "./pages/super-admin/VirtualAccountsOverview";
import PayrollApprovals from "./pages/super-admin/PayrollApprovals";
import TransactionErrors from "./pages/super-admin/TransactionErrors";
import SettingsPage from "./pages/super-admin/SettingsPage";
import SystemStatusPage from "./pages/super-admin/SystemStatusPage";
import ManualInflowPage from "./pages/super-admin/ManualInflowPage";
import StudentAccountsPage from "./pages/super-admin/StudentAccountsPage";
import StudentAccountsLanding from "./pages/super-admin/StudentAccountsLanding";
import SuperAdminTransferPage from "./pages/super-admin/SuperAdminTransferPage";
import CompanyAdminFundWallets from "./pages/wallet/CompanyAdminFundWallets";
import PayrollSchedules from "./pages/wallet/PayrollSchedules";
import CompanyTransactionsPage from "./pages/super-admin/CompanyTransactionsPage";
import DailyPayoutsPage from "./pages/super-admin/DailyPayoutsPage";

// GDL Demo Pages
import GDLLogin from "./pages/demo/gdl/GDLLogin";
import GDLOnboard from "./pages/demo/gdl/GDLOnboard";
import GDLHome from "./pages/demo/gdl/GDLHome";
import GDLWallet from "./pages/demo/gdl/GDLWallet";
import GDLInvest from "./pages/demo/gdl/GDLInvest";
import GDLStocks from "./pages/demo/gdl/GDLStocks";
import GDLBenefits from "./pages/demo/gdl/GDLBenefits";
import GDLVendors from "./pages/demo/gdl/GDLVendors";
import GDLBills from "./pages/demo/gdl/GDLBills";
import GDLProfile from "./pages/demo/gdl/GDLProfile";
import GDLAdmin from "./pages/demo/gdl/GDLAdmin";
import GDLLayout from "./pages/demo/gdl/GDLLayout";
import GDLLoanApply from "./pages/demo/gdl/GDLLoanApply";

import SuperAdminLayout from "./layouts/SuperAdminLayout";
import CompanyAdminLayout from "./layouts/CompanyAdminLayout";
import EmployeeAppLayout from "./layouts/EmployeeAppLayout";

// Platform (Pebbles for Platforms) pages
import PlatformLayout from "./layouts/PlatformLayout";
import PlatformSignup from "./pages/platform/PlatformSignup";
import PlatformDashboard from "./pages/platform/PlatformDashboard";
import PlatformGetStarted from "./pages/platform/PlatformGetStarted";
import PlatformWallets from "./pages/platform/PlatformWallets";
import PlatformTransactions from "./pages/platform/PlatformTransactions";
import PlatformApiKeys from "./pages/platform/PlatformApiKeys";
import PlatformWebhooks from "./pages/platform/PlatformWebhooks";
import PlatformGoLive from "./pages/platform/PlatformGoLive";
import PlatformSettings from "./pages/platform/PlatformSettings";
import PlatformWalletConfig from "./pages/platform/PlatformWalletConfig";
import PlatformWalletActivation from "./pages/platform/PlatformWalletActivation";
import InfrastructureHome from "./pages/infrastructure/InfrastructureHome";
import InfrastructurePayroll from "./pages/infrastructure/InfrastructurePayroll";
import InfrastructureWallets from "./pages/infrastructure/InfrastructureWallets";
import InfrastructureCollect from "./pages/infrastructure/InfrastructureCollect";
import InfrastructureConnect from "./pages/infrastructure/InfrastructureConnect";
import InfrastructureComply from "./pages/infrastructure/InfrastructureComply";
import InfrastructureAgents from "./pages/infrastructure/InfrastructureAgents";
import InfrastructureDevelopers from "./pages/infrastructure/InfrastructureDevelopers";

const ROLE_REDIRECTS = {
  super_admin: "/super-admin",
  company_admin: "/company-admin",
  employee: "/app/employee/home",
};

function ProtectedRoute({ children, allowedRole }) {
  const { isAuthenticated, loading, user } = useWalletAuth();
  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-pink-400 border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (allowedRole && user?.user_type !== allowedRole) {
    return <Navigate to={ROLE_REDIRECTS[user?.user_type] || "/login"} replace />;
  }
  return children;
}

function PublicRoute({ children }) {
  const { isAuthenticated, loading, user } = useWalletAuth();
  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-pink-400 border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (isAuthenticated) return <Navigate to={ROLE_REDIRECTS[user?.user_type] || "/super-admin"} replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<InfrastructureHome />} />
      <Route path="/estimator" element={<BenefitsEstimator />} />
      <Route path="/employers" element={<EmployersPage />} />
      <Route path="/for-employers" element={<ForEmployersPage />} />
      <Route path="/login" element={<PublicRoute><WalletLogin /></PublicRoute>} />
      <Route path="/wallet-login" element={<PublicRoute><WalletLogin /></PublicRoute>} />
      <Route path="/force-password-reset" element={<ForcePasswordReset />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/super-admin/login" element={<PublicRoute><SuperAdminLogin /></PublicRoute>} />
      <Route path="/super-admin/user-detail/:userType/:userId" element={<ProtectedRoute allowedRole="super_admin"><UserDetailPage /></ProtectedRoute>} />
      <Route path="/super-admin" element={<ProtectedRoute allowedRole="super_admin"><SuperAdminLayout /></ProtectedRoute>}>
        <Route index element={<SuperAdminDashboard />} />
        <Route path="companies" element={<CompanyManagement />} />
        <Route path="companies/:companyId" element={<CompanyDetail />} />
        <Route path="banking-partners" element={<BankingPartners />} />
        <Route path="wallet-payroll" element={<WalletPayrollControls />} />
        <Route path="vendors" element={<GlobalVendors />} />
        <Route path="reconciliation" element={<ReconciliationPage />} />
        <Route path="reports" element={<SuperAdminReports />} />
        <Route path="admins" element={<AdminManagement />} />
        <Route path="users" element={<UserManagement />} />
        <Route path="audit-logs" element={<AuditLogs />} />
        <Route path="analytics" element={<UserAnalytics />} />
        <Route path="credit-wallet" element={<WalletCredit />} />
        <Route path="virtual-accounts" element={<VirtualAccountsOverview />} />
        <Route path="approvals" element={<PayrollApprovals />} />
        <Route path="transaction-errors" element={<TransactionErrors />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="system-status" element={<SystemStatusPage />} />
        <Route path="manual-inflow" element={<ManualInflowPage />} />
        <Route path="student-accounts" element={<StudentAccountsLanding />} />
        <Route path="transfers" element={<SuperAdminTransferPage />} />
        <Route path="transactions" element={<CompanyTransactionsPage />} />
        <Route path="daily-payouts" element={<DailyPayoutsPage />} />
        <Route path="companies/:companyId/students" element={<StudentAccountsPage />} />
      </Route>
      <Route path="/company-admin" element={<ProtectedRoute allowedRole="company_admin"><CompanyAdminLayout /></ProtectedRoute>}>
        <Route index element={<CompanyDashboard />} />
        <Route path="employees" element={<EmployeeManagement />} />
        <Route path="payroll" element={<PayrollManagement />} />
        <Route path="wallets" element={<CompanyWallets />} />
        <Route path="transactions" element={<CompanyTransactions />} />
        <Route path="vendors" element={<CompanyVendors />} />
        <Route path="benefits" element={<CompanyBenefits />} />
        <Route path="payouts" element={<CompanyPayouts />} />
        <Route path="reports" element={<CompanyReports />} />
        <Route path="offer-letters" element={<OfferLettersPage />} />
        <Route path="offer-letters/create" element={<CreateOfferLetter />} />
        <Route path="offer-letters/:id" element={<OfferLetterDetail />} />
        <Route path="settings" element={<CompanySettings />} />
        <Route path="bulk-upload" element={<BulkEmployeeUpload />} />
        <Route path="banking" element={<BankingManagement />} />
        <Route path="flex-benefits" element={<FlexBenefitsAdmin />} />
        <Route path="fund-wallets" element={<CompanyAdminFundWallets />} />
        <Route path="payroll-schedules" element={<PayrollSchedules />} />
      </Route>
      <Route path="/offer/:token" element={<CandidateOfferPage />} />
      <Route path="/employee-app" element={<Navigate to="/app/employee/home" replace />} />
      <Route path="/app/employee" element={<ProtectedRoute allowedRole="employee"><EmployeeAppLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/app/employee/home" replace />} />
        <Route path="home" element={<NewEmployeeHome />} />
        <Route path="wallet" element={<EmployeeWallet />} />
        <Route path="wallet/send" element={<SendMoney />} />
        <Route path="pay" element={<EmployeePay />} />
        <Route path="benefits" element={<EmployeeBenefitsPlan />} />
        <Route path="benefits/vendors" element={<EmployeeVendors />} />
        <Route path="benefits/vendors/:vendorId" element={<VendorDetailPage />} />
        <Route path="offer-letters" element={<EmployeeOfferLetters />} />
        <Route path="flex-benefits" element={<EmployeeFlexBenefits />} />
        <Route path="transactions" element={<EmployeeTransactions />} />
        <Route path="profile" element={<EmployeeProfilePage />} />
      </Route>
      {/* GDL Demo Routes — completely outside production auth */}
      <Route path="/demo/gdl/login" element={<GDLLogin />} />
      <Route path="/demo/gdl/onboard" element={<GDLOnboard />} />
      <Route path="/demo/gdl/dashboard" element={<GDLLayout><GDLHome /></GDLLayout>} />
      <Route path="/demo/gdl/wallet" element={<GDLLayout><GDLWallet /></GDLLayout>} />
      <Route path="/demo/gdl/invest" element={<GDLLayout><GDLInvest /></GDLLayout>} />
      <Route path="/demo/gdl/stocks" element={<GDLLayout><GDLStocks /></GDLLayout>} />
      <Route path="/demo/gdl/benefits" element={<GDLLayout><GDLBenefits /></GDLLayout>} />
      <Route path="/demo/gdl/vendors" element={<GDLLayout><GDLVendors /></GDLLayout>} />
      <Route path="/demo/gdl/bills" element={<GDLLayout><GDLBills /></GDLLayout>} />
      <Route path="/demo/gdl/profile" element={<GDLLayout><GDLProfile /></GDLLayout>} />
      <Route path="/demo/gdl/admin" element={<GDLAdmin />} />
      <Route path="/demo/gdl/loan/apply" element={<GDLLayout><GDLLoanApply /></GDLLayout>} />
      <Route path="/infrastructure" element={<Navigate to="/" replace />} />
      <Route path="/infrastructure/payroll" element={<InfrastructurePayroll />} />
      <Route path="/infrastructure/wallets" element={<InfrastructureWallets />} />
      <Route path="/infrastructure/collect" element={<InfrastructureCollect />} />
      <Route path="/infrastructure/connect" element={<InfrastructureConnect />} />
      <Route path="/infrastructure/comply" element={<InfrastructureComply />} />
      <Route path="/infrastructure/agents" element={<InfrastructureAgents />} />
      <Route path="/infrastructure/developers" element={<InfrastructureDevelopers />} />
      {/* Platform (Pebbles for Platforms) routes */}
      <Route path="/platform/signup" element={<PlatformSignup />} />
      <Route path="/platform" element={<PlatformLayout />}>
        <Route index element={<Navigate to="/platform/dashboard" replace />} />
        <Route path="dashboard" element={<PlatformDashboard />} />
        <Route path="get-started" element={<PlatformGetStarted />} />
        <Route path="wallet-activation" element={<PlatformWalletActivation />} />
        <Route path="wallets" element={<PlatformWallets />} />
        <Route path="transactions" element={<PlatformTransactions />} />
        <Route path="api-keys" element={<PlatformApiKeys />} />
        <Route path="webhooks" element={<PlatformWebhooks />} />
        <Route path="go-live" element={<PlatformGoLive />} />
        <Route path="customize" element={<PlatformWalletConfig />} />
        <Route path="settings" element={<PlatformSettings />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <WalletAuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </WalletAuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
